// This program has been developed by students from the bachelor Computer Science at
// Utrecht University within the Software Project course.
// Copyright (c) Utrecht University (Department of Information and Computing Sciences)

/**
 * Generate a diamond shaped structuring element
 * @param radius Element radius
 * @returns Filter
 */
function getDiamondFilter(radius: number): [number, number][] {
  const filter: [number, number][] = [[0, 0]];
  for (let i = 0; i < radius; i++) {
    filter.push([0, i]);
    filter.push([0, -i]);
    filter.push([i, 0]);
    filter.push([-i, 0]);
    for (let j = 1; j < i; j++) {
      const k = i - j;
      filter.push([j, -k]);
      filter.push([j, k]);
      filter.push([-j, -k]);
      filter.push([-j, k]);
    }
  }

  return filter;
}

/**
 * Whether the filter is a hit or miss
 * @param x x position
 * @param y y position
 * @param image Image
 * @param filter Filter list
 * @returns Hit or miss boolean
 */
function isHit(
  x: number,
  y: number,
  image: number[][],
  filter: [number, number][]
): boolean {
  for (let i = 0; i < filter.length; i++) {
    const [u, v] = filter[i];
    if (
      x + u >= 0 &&
      x + u < image.length &&
      y + v >= 0 &&
      y + v < image[0].length &&
      image[x + u][y + v] == 1
    )
      return true;
  }
  return false;
}

/**
 * Whether the filter is a fit or not
 * @param x x position
 * @param y y position
 * @param image Image
 * @param filter Filter list
 * @returns Hit or miss boolean
 */
function isFit(
  x: number,
  y: number,
  image: number[][],
  filter: [number, number][]
): boolean {
  for (let i = 0; i < filter.length; i++) {
    const [u, v] = filter[i];
    if (
      x + u >= 0 &&
      x + u < image.length &&
      y + v >= 0 &&
      y + v < image[0].length &&
      image[x + u][y + v] != 1
    )
      return false;
  }
  return true;
}

/**
 * Morphological dilation (growing)
 * @param arr Dilation array
 * @param strength Dilation strength
 * @returns Dilation
 */
function dilate(arr: number[][], strength: number): number[][] {
  const width = arr.length;
  const height = arr[0].length;
  const res: number[][] = [];
  const filter = getDiamondFilter(strength);

  for (let x = 0; x < width; x++) {
    res[x] = [];
    for (let y = 0; y < height; y++) {
      if (isHit(x, y, arr, filter)) {
        res[x][y] = 1;
      } else {
        res[x][y] = 0;
      }
    }
  }

  return res;
}

/**
 * Morphological erosion (shrinking)
 * @param arr Dilation array
 * @param strength Dilation strength
 * @returns Erosion
 */
function erode(arr: number[][], strength: number): number[][] {
  const width = arr.length;
  const height = arr[0].length;
  const res: number[][] = [];
  const filter = getDiamondFilter(strength);

  for (let x = 0; x < width; x++) {
    res[x] = [];
    for (let y = 0; y < height; y++) {
      if (isFit(x, y, arr, filter)) {
        res[x][y] = 1;
      } else {
        res[x][y] = 0;
      }
    }
  }

  return res;
}

export { erode, dilate };
